import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Copy, Linkedin, Megaphone } from 'lucide-react';
import { linkedInShareQuizUrl, quizUrl } from '@/lib/api';
import { Button } from '@/components/ui';

/**
 * Invites learners to spread a quiz: share it on LinkedIn, copy the link,
 * or get campaign posters. Org-only quizzes skip the public sharing and
 * offer posters for notice boards inside the org.
 */
export function ShareQuizCard({
  slug,
  audience,
  heading,
  text,
}: {
  slug: string;
  audience: 'public' | 'org';
  heading: string;
  text: string;
}) {
  const [copied, setCopied] = useState(false);
  const isPublic = audience === 'public';
  return (
    <section className="rounded-[var(--radius)] border-2 border-primary/60 bg-primary/10 p-5">
      <div className="flex items-start gap-3">
        <Megaphone className="mt-0.5 size-6 shrink-0 text-primary" aria-hidden />
        <div>
          <h2 className="text-lg font-semibold">{heading}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{text}</p>
        </div>
      </div>
      <div className={`mt-4 grid gap-3 ${isPublic ? 'sm:grid-cols-3' : ''}`}>
        {isPublic && (
          <a
            href={linkedInShareQuizUrl(slug)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 rounded-[var(--radius)] bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:opacity-90"
          >
            <Linkedin className="size-4" aria-hidden /> Share on LinkedIn
          </a>
        )}
        {isPublic && (
          <Button
            variant="secondary"
            onClick={async () => {
              await navigator.clipboard.writeText(quizUrl(slug));
              setCopied(true);
              setTimeout(() => setCopied(false), 2000);
            }}
          >
            {copied ? (
              <Check className="size-4" aria-hidden />
            ) : (
              <Copy className="size-4" aria-hidden />
            )}
            {copied ? 'Link copied' : 'Copy link'}
          </Button>
        )}
        <Link
          to={`/q/${slug}/poster`}
          className="inline-flex items-center justify-center gap-2 rounded-[var(--radius)] border border-border bg-card px-4 py-2 text-sm font-medium hover:bg-muted"
        >
          Get campaign posters
        </Link>
      </div>
    </section>
  );
}
